import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { requestPasswordReset } from "@/lib/auth/password-reset-api";

// Frontend mirror of the backend throttle window. Disables the submit button
// for 60s after a successful request so users can't spam the endpoint from
// this tab; the backend ([FIN-20]) enforces the real throttle.
const COOLDOWN_SECONDS = 60;

export function PasswordResetRequestPage() {
  const location = useLocation();
  // The Topbar account menu's "Change password" item hands the signed-in
  // user's email here so they don't have to retype it.
  const prefillEmail =
    typeof (location.state as { email?: unknown } | null)?.email === "string"
      ? ((location.state as { email: string }).email)
      : "";
  const [email, setEmail] = useState(prefillEmail);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const cooldownTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (cooldownTimer.current != null) {
        window.clearInterval(cooldownTimer.current);
      }
    };
  }, []);

  function startCooldown() {
    setCooldown(COOLDOWN_SECONDS);
    if (cooldownTimer.current != null) {
      window.clearInterval(cooldownTimer.current);
    }
    cooldownTimer.current = window.setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) {
          if (cooldownTimer.current != null) {
            window.clearInterval(cooldownTimer.current);
            cooldownTimer.current = null;
          }
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting || cooldown > 0) return;
    setError(null);

    const trimmed = email.trim();
    if (!trimmed) {
      setError("Please enter your email.");
      return;
    }

    setSubmitting(true);
    try {
      // Endpoint always returns 204 to prevent enumeration; we only care
      // whether the call reached the server.
      await requestPasswordReset({ email: trimmed });
      setSubmitted(true);
      startCooldown();
    } catch {
      setError("Couldn't reach the server. Check your connection and retry.");
    } finally {
      setSubmitting(false);
    }
  }

  const disabled = submitting || cooldown > 0;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-1 text-center">
          <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
            Finance
          </p>
          <h1 className="text-xl font-semibold tracking-tight">
            Reset your password
          </h1>
        </div>

        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-sm normal-case tracking-normal text-foreground">
              Request a reset link
            </CardTitle>
            <CardDescription>
              Enter the email an administrator invited you with. We'll send a
              link to reset your password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                />
              </div>

              {submitted ? (
                <p
                  role="status"
                  className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-foreground"
                >
                  If that email is registered, a reset link has been sent.
                  Please check your inbox.
                </p>
              ) : null}

              {error ? (
                <p
                  role="alert"
                  className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
                >
                  {error}
                </p>
              ) : null}

              <Button type="submit" className="w-full" disabled={disabled}>
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : cooldown > 0 ? (
                  `Resend in ${cooldown}s`
                ) : submitted ? (
                  "Resend link"
                ) : (
                  "Send reset link"
                )}
              </Button>

              <p className="text-center text-xs text-muted-foreground">
                Remembered your password?{" "}
                <Link
                  to="/login"
                  className="text-foreground hover:underline"
                >
                  Back to sign in
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
